import { useState } from 'react';
import {
  SidebarState,
  DEFAULT_TABS,
  getDefaultTabForRole,
  DashboardTab,
} from '../types/dashboard';
import { useAuth } from './useAuth';
import { useSessionState } from './useSessionState';
import { SessionKey, StorageKey } from '@/types/storage';
import StorageService from '@/utils/storage';

export function useSidebar() {
  const { user } = useAuth();

  // Determine the default tab based on user role
  const getInitialTab = (): string => {
    if (!user?.role) {
      return DashboardTab.MY_BOARD; // Fallback if role not available
    }
    return getDefaultTabForRole(user.role);
  };

  const defaultTab = getInitialTab();

  // Migrate removed tabs ('home', 'dashboard') from any persisted sessionStorage value
  const migrateTab = (tab: string): string => {
    if (tab === 'home' || tab === DashboardTab.DASHBOARD) {
      return defaultTab;
    }
    return tab;
  };

  const [persistedTab, setPersistedTab] = useSessionState<string>(
    SessionKey.LB_TAB,
    defaultTab
  );

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() =>
    StorageService.getOrDefault(StorageKey.SIDEBAR_STATE, true)
  );
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [activeTabInternal, setActiveTabInternal] = useState<string>(
    migrateTab(persistedTab)
  );

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      StorageService.set(StorageKey.SIDEBAR_STATE, next);
      return next;
    });
  };

  const setActiveTab = (tabId: string) => {
    setPersistedTab(tabId);
    setActiveTabInternal(tabId);
  };

  const addTab = (tab: any) => {
    setTabs(prev => [...prev, tab]);
  };

  const removeTab = (tabId: string) => {
    const fallbackTab = getInitialTab();
    setTabs(prev => prev.filter(t => t.id !== tabId));
    if (activeTabInternal === tabId) {
      const newTab = tabs[0]?.id || fallbackTab;
      setPersistedTab(newTab);
      setActiveTabInternal(newTab);
    }
  };

  const sidebarState: SidebarState = {
    isCollapsed,
    activeTab: activeTabInternal,
    tabs,
  };

  return {
    sidebarState,
    toggleSidebar,
    setActiveTab,
    addTab,
    removeTab,
    isCollapsed,
    activeTab: activeTabInternal,
    tabs,
  };
}
